import React from 'react';
import { useTranslation } from 'react-i18next';
import { capitalize } from 'helpers/helpers';
import { TextField, useTheme } from '@mui/material';
import {
    WidgetContent,
    WidgetT,
} from 'routes/Dashboard/components/WidgetsLayout/helpers';
import Flex from 'ui/Flex/Flex';
import ColorPicker from 'ui/ColorPicker/ColorPicker';
import { useEditNavLinkForm } from './hooks/navLinkForm';
import GeneralModal from 'components/SharedComponents/Modals/GeneralModal';

type Props = {
    widget: WidgetT;
    isEditModalOpen: boolean;
    setIsEditModalOpen: (arg1: boolean) => void;
    onEditWidget: (arg1: { widget: WidgetT; content: WidgetContent }) => void;
};

export default function EditNavLinkModal({
    widget,
    isEditModalOpen,
    setIsEditModalOpen,
    onEditWidget,
}: Props): React.ReactElement {
    const { t } = useTranslation();
    const theme = useTheme();

    const {
        text,
        url,
        fontColor,
        backgroundColor,
        errors,
        handleTextChange,
        handleUrlChange,
        handleFontColorChange,
        handleBackgroundColorChange,
        validateFields,
    } = useEditNavLinkForm(widget, t, theme);

    const onConfirmAction = () => {
        if (validateFields()) {
            onEditWidget({
                widget,
                content: { text, url, fontColor, backgroundColor },
            });
            setIsEditModalOpen(false);
        }
    };

    const confirmButton = {
        text: t('widget.update'),
        onClick: onConfirmAction,
    };
    const cancelButton = {
        text: t('common.cancel'),
        onClick: () => setIsEditModalOpen(false),
    };
    return (
        <GeneralModal
            isOpen={isEditModalOpen}
            onClose={() => setIsEditModalOpen(false)}
            width="sm"
            textButton={cancelButton}
            containedButton={confirmButton}
            showCloseButton={false}
        >
            <>
                <TextField
                    value={text}
                    onChange={handleTextChange}
                    autoFocus
                    className="form-control"
                    variant="outlined"
                    margin="dense"
                    size="small"
                    fullWidth
                    key="text"
                    label={capitalize(t('widget.navlink.text'))}
                    name="title"
                    type="text"
                    error={!!errors.text}
                    helperText={errors.text}
                />
                <TextField
                    value={url}
                    onChange={handleUrlChange}
                    className="form-control"
                    variant="outlined"
                    margin="dense"
                    size="small"
                    fullWidth
                    key="url"
                    label={capitalize(t('widget.navlink.url'))}
                    name="url"
                    type="text"
                    error={!!errors.url}
                    helperText={errors.url}
                />
                <Flex
                    align="center"
                    marginTop={theme.spacing(1)}
                    gap={theme.spacing(2)}
                >
                    <ColorPicker
                        value={fontColor}
                        setValue={handleFontColorChange}
                        label={t('widget.navlink.fontColor')}
                        error={errors.fontColor}
                    />
                    <ColorPicker
                        value={backgroundColor}
                        setValue={handleBackgroundColorChange}
                        label={t('widget.navlink.backgroundColor')}
                        error={errors.backgroundColor}
                    />
                </Flex>
            </>
        </GeneralModal>
    );
}
